import {isFeatureEnabled} from 'feature-flags';

import {useState} from 'react';
import {useLocation} from 'react-router-dom';

import {featureFlags} from '@dms/feature-flags';
import {sourcingRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

import {useSourcingTable} from '../../../components/SourcingTable/hooks/useSourcingTable';
import {
  SourcingViewMode,
  sourcingTableSessionStorage,
} from '../../../components/SourcingTable/utils/sourcingTableSessionStorage';
import {rememberSourcingPrevUrl} from '../../../constants/rememberSourcingPrevUrl';
import {SourcingType} from '../../../hooks/useSourcingType';

export function useAllOffersMapState(options: {
  source: SourcingType;
  selectedAdId: string | undefined;
  tableName: ReturnType<typeof useSourcingTable>[0]['tableName'];
  clearListSelection: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedViewMode, setSelectedViewMode] = useState<SourcingViewMode>(() =>
    sourcingTableSessionStorage.getAllOffersViewMode()
  );
  const isClassifiedsMapEnabled = isFeatureEnabled(featureFlags.SOURCING_CLASSIFIEDS_MAP);
  const isMapView =
    options.source === SourcingType.CLASSIFIEDS &&
    isClassifiedsMapEnabled &&
    selectedViewMode === 'map';
  const visibleViewMode = selectedViewMode === 'map' && !isMapView ? 'table' : selectedViewMode;
  const [selectedMapAdId, setSelectedMapAdId] = useState<string | null | undefined>(() => {
    const savedMapAdId = sourcingTableSessionStorage.getMapSelectedVehicleId();
    return savedMapAdId === undefined
      ? (sourcingTableSessionStorage.getLastVisitedVehicleId('all-vehicles') ?? undefined)
      : savedMapAdId;
  });
  // Keep a selected list row as the initial map preview until the user picks or clears a map ad.
  const savedMapPreviewAdId =
    selectedMapAdId === undefined ? options.selectedAdId : selectedMapAdId;
  const onChangeViewMode = (mode: SourcingViewMode) => {
    if (mode === 'map' && !isMapView) {
      // The map must open on the car selected in the list, even if it held an older selection.
      setSelectedMapAdId(options.selectedAdId ?? null);
      sourcingTableSessionStorage.setMapSelectedVehicleId(options.selectedAdId ?? null);
    } else if (isMapView && mode !== 'map' && savedMapPreviewAdId !== options.selectedAdId) {
      // Let the list restore the map selection once its rows have loaded again.
      options.clearListSelection();
    }
    setSelectedViewMode(mode);
    sourcingTableSessionStorage.setAllOffersViewMode(mode);
  };
  const selectMapAd = (adId: string) => {
    setSelectedMapAdId(adId);
    sourcingTableSessionStorage.setMapSelectedVehicleId(adId);
    sourcingTableSessionStorage.setLastVisitedVehicleId(options.tableName, adId);
  };
  const onOpenDetail = (adId: string) => {
    selectMapAd(adId);
    rememberSourcingPrevUrl(location);
    navigate(
      composePath(
        options.source === SourcingType.AUCTIONS
          ? sourcingRoutes.auctionDetail
          : sourcingRoutes.vehicleDetail,
        {params: {adId}}
      )
    );
  };
  const onSelectMapAd = (adId: string, mapPreviewAdId: string | null) => {
    if (mapPreviewAdId === adId) {
      setSelectedMapAdId(null);
      sourcingTableSessionStorage.setMapSelectedVehicleId(null);
    } else {
      selectMapAd(adId);
    }
  };

  return {
    isMapView,
    isClassifiedsMapEnabled,
    visibleViewMode,
    savedMapPreviewAdId,
    onChangeViewMode,
    onSelectMapAd,
    onOpenDetail,
  };
}
